/**
 * What this person has searched for, on this device.
 *
 * ## Why the browser and not the server
 *
 * Because it is a convenience, not a record. Kept server-side it would be a
 * per-account log of everything anybody has ever looked for - a thing worth
 * stealing, worth subpoenaing, and worth having to explain in a privacy policy
 * that currently says local storage holds a volume level and a playhead
 * position. It would also need a table, an endpoint and a retention policy to
 * do a job that eight strings in `localStorage` do completely.
 *
 * The cost is that it does not follow somebody to their phone. For a list whose
 * entire purpose is "the thing I typed a minute ago", that is not a cost.
 *
 * ## Every path is guarded
 *
 * `localStorage` throws rather than returning null in a few real situations -
 * Safari's private mode historically, and any browser set to block site data -
 * and a search box that throws on focus is a worse failure than one that
 * forgets. Every function here fails to "no history" and never propagates.
 */

const KEY = 'gv:searches';

/**
 * How many are kept.
 *
 * Long enough to cover a session's worth of looking, short enough that the list
 * is still scannable at a glance and never scrolls. Past this it stops being
 * "what I just did" and becomes an archive nobody asked for.
 */
const CAP = 8;

/** Longer than this is not a search anybody will want to repeat. */
const MAX_LENGTH = 80;

export function recent(): string[] {
  try {
    const raw = localStorage.getItem(KEY);

    if (raw === null) return [];

    const parsed: unknown = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry): entry is string => typeof entry === 'string').slice(0, CAP);
  } catch {
    return [];
  }
}

/**
 * Put a term at the top, or move it there.
 *
 * Case-insensitive de-duplication, keeping the spelling just used: searching
 * "OSHUN" after "oshun" should leave one entry reading the way it was last
 * typed, not two differing by a shift key.
 */
export function remember(term: string): void {
  const trimmed = term.trim().slice(0, MAX_LENGTH);

  if (trimmed === '') return;

  const lower = trimmed.toLowerCase();
  const next = [trimmed, ...recent().filter((entry) => entry.toLowerCase() !== lower)].slice(0, CAP);

  write(next);
}

export function forget(term: string): void {
  const lower = term.toLowerCase();

  write(recent().filter((entry) => entry.toLowerCase() !== lower));
}

export function clear(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do, and nothing worth telling anybody about.
  }
}

function write(entries: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(entries));
  } catch {
    // Storage full, or blocked. The search still works; it just forgets.
  }
}
